import { isNumeric, lastChar } from 'util/StringUtils'
import { makeDollarsParser, makePriceValuationParser, makeRangeParser } from 'util/grammar'
import numeral from 'numeral'

interface SharesVolumeParserRangeSuccess {
  isRange: true
  value: { min: number; max: number }
}

interface SharesVolumeParserSingleSuccess {
  isRange: false
  value: number
}
type SharesVolumeParserSuccess = (SharesVolumeParserRangeSuccess | SharesVolumeParserSingleSuccess) & {
  type: 'shares' | 'dollars'
  isError: false
}

interface ParserFailure {
  isError: true
  error: string
}

interface ParserSuccess {
  isError: false
}

interface NearleyShareVolumeParseSingle {
  value: string
  isShare: boolean
  hasDollarSign: boolean
}

interface RangeResult {
  min: NearleyShareVolumeParseSingle
  max: NearleyShareVolumeParseSingle
}

type NearlyShareVolumeParseResult = (NearleyShareVolumeParseSingle | RangeResult) & { isError: false }

type SharesVolumeParserResult = SharesVolumeParserSuccess | ParserFailure

type ShareVolumeParserType = (value: string) => SharesVolumeParserResult

interface DollarsParserSuccess extends ParserSuccess {
  value: number
}

type DollarsParserResult = ParserFailure | DollarsParserSuccess

export const parseDollars = (raw: string): DollarsParserResult => {
  const parser = makeDollarsParser()

  try {
    parser.feed(raw)
  } catch {
    return { isError: true, error: 'Failed to parse input string.' }
  }

  const results = parser.results
  return { isError: false, value: numeral(results[0].toLowerCase()).value() }
}

export const parseSharesVolume: ShareVolumeParserType = (value: string) => {
  try {
    const parser = makeRangeParser()
    parser.feed(value)
    const results = parser.results
    if (!results.length) {
      return { error: 'Invalid', isError: true }
    }
    const result: NearlyShareVolumeParseResult = parser.results[0][0]
    // eslint-disable-next-line no-prototype-builtins
    if (result.hasOwnProperty('min')) {
      // Range case
      const rangeResult = result as RangeResult
      const isShare = rangeResult.max.isShare || rangeResult.min.isShare
      const isDollar = rangeResult.max.hasDollarSign || rangeResult.min.hasDollarSign
      if (isShare && isDollar) {
        return { error: 'Cannot mix dollars and shares', isError: true }
      }
      const tpe = isShare ? 'shares' : 'dollars'
      const maxVal = numeral(rangeResult.max.value.toLowerCase()).value()
      const minVal = () => {
        //Grab the scale delimeter (e.g. 'B') from max value if there is no scale on min value
        const minInputString = rangeResult.min.value.toLowerCase()
        const maxInputString = rangeResult.max.value.toLowerCase()
        if (isShare || isNumeric(maxInputString) || !isNumeric(minInputString)) return numeral(minInputString).value()
        return numeral(minInputString + lastChar(maxInputString)).value()
      }
      const val = { min: minVal(), max: maxVal }
      return { isRange: true, type: tpe, value: val, isError: false }
    }
    // Single case
    const singleResult = result as NearleyShareVolumeParseSingle
    const isShare = singleResult.isShare || singleResult.isShare
    const isDollar = singleResult.hasDollarSign || singleResult.hasDollarSign
    if (isShare && isDollar) {
      return { error: 'Cannot mix dollars and shares', isError: true }
    }
    const tpe = isShare ? 'shares' : 'dollars'
    const val = numeral(singleResult.value.toLowerCase()).value()
    return { isRange: false, type: tpe, value: val, isError: false }
  } catch (err) {
    return { error: err, isError: true }
  }
}

interface ValuationPriceSuccess {
  isNts: boolean
  isNtb: boolean
  value: number
  type: 'price' | 'valuation'
  isError: false
}

type ValuationPriceResult = ValuationPriceSuccess | ParserFailure

type ValuationSharePriceParserType = (value: string) => ValuationPriceResult

export const parseValuationSharePrice: ValuationSharePriceParserType = (value: string) => {
  try {
    const parser = makePriceValuationParser()
    parser.feed(value)
    const results = parser.results
    if (!results.length) {
      return { error: `Invalid - failed to parse ${value}`, isError: true }
    }
    const result: { isNtb: boolean; isNts: boolean; value: string } = results[0]
    const numberValue = numeral(result.value.toLowerCase()).value()
    return {
      ...result,
      value: numberValue,
      type: numberValue >= 1e7 ? 'valuation' : 'price',
      isError: false,
    }
  } catch (err) {
    return { error: err, isError: true }
  }
}

export const formatMoney = (money: string | number) => {
  return numeral(money)
    .format('($0[.]000a)')
    .toUpperCase()
}

export const formatPriceOrValuation = (priceOrValuation: string) => {
  const parsedPriceOrValuation = parseValuationSharePrice(priceOrValuation)
  if (parsedPriceOrValuation.isError) {
    return priceOrValuation
  }

  if (parsedPriceOrValuation.type === 'valuation') {
    return `${formatMoney(parsedPriceOrValuation.value)} val`
  }

  const { isNts, isNtb } = parsedPriceOrValuation
  return `${formatMoney(parsedPriceOrValuation.value)} ${isNtb ? 'NTB' : ''}${isNts ? 'NTS' : ''}${
    !isNts && !isNtb ? 'gross' : ''
  } pps`
}

export const formatShares = (shares: string | number) => {
  return numeral(shares).format('0,0')
}

export const formatSharesOrSize = (sharesOrSize: string) => {
  const parsedSharesOrSize = parseSharesVolume(sharesOrSize)
  if (parsedSharesOrSize.isError) {
    return sharesOrSize
  }
  if (parsedSharesOrSize.type === 'shares') {
    if (parsedSharesOrSize.isRange) {
      return `${formatShares(parsedSharesOrSize.value.min)} shares - ${formatShares(
        parsedSharesOrSize.value.max
      )} shares`
    }
    return `${formatShares(parsedSharesOrSize.value)} shares`
  }
  if (parsedSharesOrSize.isRange) {
    return `${formatMoney(parsedSharesOrSize.value.min)} - ${formatMoney(parsedSharesOrSize.value.max)}`
  }
  return `${formatMoney(parsedSharesOrSize.value)}`
}
